import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
// import { useIsMobile } from '@/hooks/use-mobile'; // No longer needed for layout decision
// import { Dialog, DialogContent } from "@/components/ui/dialog" // Removed

interface Entity {
    entity_id?: string;
    code: string;
    description: string;
    content?: string;
    alias?: string[];
    status: string;
    jd_level?: 'L1' | 'L2' | 'L3';
}

interface EntityEditorProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: Entity;
}

export function EntityEditor({ isOpen, onClose, initialData }: EntityEditorProps) {
    // const isMobile = useIsMobile(); // Removed
    const [code, setCode] = useState('');
    const [description, setDescription] = useState('');
    const [content, setContent] = useState('');
    const [aliasInput, setAliasInput] = useState('');
    const [status, setStatus] = useState('ACTIVE');
    const [jdLevel, setJdLevel] = useState<'L1' | 'L2' | 'L3' | undefined>(undefined);
    const [isContentOpen, setIsContentOpen] = useState(false);

    // Pickers state
    const [isStatusPickerOpen, setIsStatusPickerOpen] = useState(false);
    const [isJdLevelPickerOpen, setIsJdLevelPickerOpen] = useState(false);

    const queryClient = useQueryClient();



    useEffect(() => {
        if (isOpen && initialData) {
            setCode(initialData.code);
            setDescription(initialData.description);
            setContent(initialData.content || '');
            setAliasInput(initialData.alias ? initialData.alias.join(', ') : '');
            setStatus(initialData.status || 'ACTIVE');
            setJdLevel(initialData.jd_level);
        } else if (isOpen) {
            setCode('');
            setDescription('');
            setContent('');
            setAliasInput('');
            setStatus('ACTIVE');
            setJdLevel(undefined);
        }
    }, [isOpen, initialData]);

    const createMutation = useMutation({
        mutationFn: api.createEntity,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['entities'] });
            onClose();
        },
    });

    const updateMutation = useMutation({
        mutationFn: (data: any) => api.updateEntity(initialData!.entity_id!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['entities'] });
            onClose();
        },
    });

    const handleSave = () => {
        const alias = aliasInput.split(',').map(s => s.trim()).filter(s => s.length > 0);
        const data = { code, description, content, alias, status, jd_level: jdLevel };

        if (initialData?.entity_id) {
            updateMutation.mutate(data);
        } else {
            createMutation.mutate(data);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            handleSave();
        }
        if (e.key === 'Escape') {
            // Always handle manual closing since we are not using Dialog anymore
            if (isContentOpen) {
                setIsContentOpen(false);
            } else {
                onClose();
            }
        }
    };

    if (!isOpen) return null;

    const isPending = createMutation.isPending || updateMutation.isPending;

    // --- Content Renderer ---
    const renderEditorContent = () => (
        <div className="flex-1 flex flex-col h-full bg-white dark:bg-zinc-950 overflow-hidden">
            {isContentOpen ? (
                <div className="flex-1 flex flex-col w-full h-full animate-in fade-in slide-in-from-bottom-4 duration-200">
                    <div className="flex items-center justify-between p-4 border-b border-zinc-100 dark:border-zinc-900 shrink-0">
                        <button
                            onClick={() => setIsContentOpen(false)}
                            className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                            <span className="font-medium">Back</span>
                        </button>
                        <span className="font-medium text-zinc-900 dark:text-zinc-100">Content Editor</span>
                        <div className="w-20" />
                    </div>
                    <div className="flex-1 p-8 overflow-y-auto">
                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="w-full h-full bg-transparent border-none focus:ring-0 outline-none p-0 text-base leading-relaxed text-zinc-900 dark:text-zinc-100 resize-none placeholder:text-zinc-300 dark:placeholder:text-zinc-700 font-mono"
                            placeholder="Write your content here..."
                            autoFocus
                            inputMode="text"
                        />
                    </div>
                </div>
            ) : (
                <div className="flex-1 w-full max-w-3xl mx-auto relative flex flex-col overflow-hidden">
                    <div className="flex-1 overflow-y-auto p-4 md:p-8 flex flex-col">
                        {/* Code (Title) */}
                        <input
                            type="text"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="w-full text-3xl md:text-4xl font-bold bg-transparent border-none focus:ring-0 outline-none placeholder:text-zinc-300 dark:placeholder:text-zinc-700 text-zinc-900 dark:text-zinc-100 p-0 mb-4 shrink-0"
                            placeholder="Entity Name"
                            autoFocus
                            autoCapitalize="words"
                            autoCorrect="off"
                            inputMode="text"
                        />

                        {/* Aliases */}
                        <div className="flex items-center gap-2 mb-6 text-zinc-500 dark:text-zinc-400 shrink-0">
                            <span className="text-sm font-medium shrink-0">AKA:</span>
                            <input
                                type="text"
                                value={aliasInput}
                                onChange={(e) => setAliasInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className="w-full bg-transparent border-none focus:ring-0 outline-none p-0 text-sm placeholder:text-zinc-300 dark:placeholder:text-zinc-700"
                                placeholder="Aliases (comma separated)..."
                                autoCapitalize="words"
                                autoCorrect="off"
                            />
                        </div>

                        {/* Description */}
                        <div className="flex-1 flex flex-col mb-6">
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className="w-full h-full bg-transparent border-none focus:ring-0 outline-none p-0 text-base md:text-lg text-zinc-600 dark:text-zinc-300 resize-none placeholder:text-zinc-300 dark:placeholder:text-zinc-700"
                                placeholder="Short description..."
                            />
                        </div>
                    </div>

                    {/* Toolbar */}
                    <div className="p-4 pb-8 md:pb-4 flex flex-col gap-3 bg-white dark:bg-zinc-950 border-t border-zinc-100 dark:border-zinc-900 z-20 shrink-0">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex gap-1.5 items-center flex-wrap">
                                {/* JD Level Selector */}
                                <div className="relative">
                                    <button
                                        onClick={() => setIsJdLevelPickerOpen(!isJdLevelPickerOpen)}
                                        className={`flex items-center gap-1.5 px-2.5 py-2 hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-lg transition-colors ${jdLevel ? 'text-blue-600 dark:text-blue-400' : 'text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100'}`}
                                    >
                                        <span className="text-xs font-medium hidden sm:inline">{jdLevel || 'Level'}</span>
                                    </button>

                                    {isJdLevelPickerOpen && (
                                        <>
                                            <div className="fixed inset-0 z-30" onClick={() => setIsJdLevelPickerOpen(false)} />
                                            <div className="absolute bottom-full left-0 mb-2 w-24 bg-white dark:bg-zinc-900 rounded-lg shadow-xl border border-zinc-200 dark:border-zinc-800 py-1 z-40 flex flex-col">
                                                <button
                                                    onClick={() => { setJdLevel(undefined); setIsJdLevelPickerOpen(false); }}
                                                    className={`px-4 py-2 text-left text-sm font-medium hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors ${!jdLevel ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30' : 'text-zinc-500 dark:text-zinc-400'}`}
                                                >
                                                    None
                                                </button>
                                                {['L1', 'L2', 'L3'].map((level) => (
                                                    <button
                                                        key={level}
                                                        onClick={() => { setJdLevel(level as any); setIsJdLevelPickerOpen(false); }}
                                                        className={`px-4 py-2 text-left text-sm font-medium hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors ${jdLevel === level ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30' : 'text-zinc-500 dark:text-zinc-400'}`}
                                                    >
                                                        {level}
                                                    </button>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Status Selector */}
                                <div className="relative">
                                    <button
                                        onClick={() => setIsStatusPickerOpen(!isStatusPickerOpen)}
                                        className="flex items-center gap-1.5 px-2.5 py-2 text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-lg transition-colors"
                                    >
                                        <div className={`w-2 h-2 rounded-full ${status === 'ACTIVE' ? 'bg-green-500' : status === 'INACTIVE' ? 'bg-amber-500' : 'bg-zinc-400'}`} />
                                    </button>

                                    {isStatusPickerOpen && (
                                        <>
                                            <div className="fixed inset-0 z-30" onClick={() => setIsStatusPickerOpen(false)} />
                                            <div className="absolute bottom-full left-0 mb-2 w-48 bg-white dark:bg-zinc-900 rounded-lg shadow-xl border border-zinc-200 dark:border-zinc-800 py-1 z-40 flex flex-col [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
                                                {['INACTIVE', 'ACTIVE', 'ARCHIVED'].map((s) => (
                                                    <button
                                                        key={s}
                                                        onClick={() => { setStatus(s); setIsStatusPickerOpen(false); }}
                                                        className={`flex items-center gap-2 px-4 py-2 text-left text-sm font-medium hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors uppercase ${status === s ? 'text-zinc-900 dark:text-zinc-100 bg-zinc-50 dark:bg-zinc-800/50' : 'text-zinc-500 dark:text-zinc-400'}`}
                                                    >
                                                        <div className={`w-2 h-2 rounded-full ${s === 'ACTIVE' ? 'bg-green-500' : s === 'INACTIVE' ? 'bg-amber-500' : 'bg-zinc-400'}`} />
                                                        {s}
                                                    </button>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Content Toggle */}
                                <button
                                    onClick={() => setIsContentOpen(true)}
                                    className={`flex items-center gap-1.5 px-2.5 py-2 hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-lg transition-colors ${content && content.trim().length > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100'}`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill={content && content.trim().length > 0 ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                                </button>
                            </div>

                            <button
                                disabled={!code.trim() || isPending}
                                onClick={handleSave}
                                className="px-6 py-2.5 text-sm font-medium text-white bg-zinc-900 rounded-full hover:bg-zinc-800 disabled:opacity-50 dark:bg-zinc-50 dark:text-zinc-900 transition-all hover:shadow-lg active:scale-95"
                            >
                                {isPending ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    // Render as bottom sheet / drawer (unified)
    return (
        <div className="fixed inset-0 z-[100] flex flex-col justify-end">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose} />
            <div className="relative w-full h-[80vh] bg-white dark:bg-zinc-950 shadow-2xl border-t border-zinc-200 dark:border-zinc-800 animate-in slide-in-from-bottom duration-300 flex flex-col rounded-t-xl overflow-hidden pb-safe">
                <div className="flex justify-center pt-2 pb-1" onDoubleClick={onClose} >
                    <div className="w-12 h-1.5 bg-zinc-300 dark:bg-zinc-700 rounded-full" />
                </div>
                {renderEditorContent()}
            </div>
        </div>
    );
}
